// TotalMsbTransactionReport.jsx

import React, { useEffect, useState } from 'react';
import {
  Button,
  Box,
  CircularProgress,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import { DataGrid } from '@mui/x-data-grid';
import { format } from 'date-fns';
import { jwtDecode } from 'jwt-decode';
import BackButton from '../../components/BackButton';
// [CASH-MODE] UPI / Cash filter + indication
import PaymentModeFilter from '../../components/PaymentModeFilter';
import PaymentModeChip from '../../components/PaymentModeChip';
import PaymentModeTotals from '../../components/PaymentModeTotals';
import { PAYMENT_MODES } from '../../constants/paymentModes';
import { paymentModeParam, paymentModeLabel, filterRowsByMode, totalsByModeFromRows } from '../../utils/paymentMode';

const TotalMsbTransactionReport = () => {
  const { userId: paramUserId } = useParams();
  const todayStr = new Date().toISOString().split('T')[0];
  const thirtyDaysAgoStr = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  const [startDate, setStartDate] = useState(thirtyDaysAgoStr);
  const [endDate, setEndDate] = useState(todayStr);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [shops, setShops] = useState([]);
  const [selectedShop, setSelectedShop] = useState('');
  const [summary, setSummary] = useState({
    totalTransactions: 0,
    totalAmount: 0,
    successfulTransactions: 0,
    failedTransactions: 0
  });
  const [msbInfo, setMsbInfo] = useState({
    userLevel: null,
    bypassRestrictions: false,
    totalShops: 0,
    isFiltered: false,
    filteredShops: 0
  });
  const [loading, setLoading] = useState(false);
  const [shopsLoading, setShopsLoading] = useState(false);
  const [paymentMode, setPaymentMode] = useState(PAYMENT_MODES.ALL); // [CASH-MODE]
  const [totalsByMode, setTotalsByMode] = useState(null); // [CASH-MODE]

  // Get current user info & target userId from URL params
  const token = sessionStorage.getItem('token');
  let loggedInUserId = '';
  if (token) {
    try {
      const decodedToken = jwtDecode(token);
      loggedInUserId = decodedToken.userId;
    } catch (error) {
      console.error('Error decoding token:', error);
    }
  }

  const userId = paramUserId || loggedInUserId;

  useEffect(() => {
    if (userId) {
      fetchShopsUnderMsb();
      fetchTransactionData();
    } else {
      setDialogMessage('User ID is missing. Please login again.');
      setOpenDialog(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleShopChange = (event) => {
    setSelectedShop(event.target.value);
  };

  const handleResetFilter = () => {
    setSelectedShop('');
  };

  const fetchShopsUnderMsb = async () => {
    setShopsLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/msbReport/getShopsUnderMsb/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 200 && response.data.status === 'SUCCESS') {
        setShops(response.data.data.shops || []);
      }
    } catch (error) {
      console.error('Error fetching shops:', error);
    }
    setShopsLoading(false);
  };

  const fetchTransactionData = async () => {
    setLoading(true);
    
    try {
      const requestData = {
        startDate,
        endDate,
        ...(selectedShop && { shopId: selectedShop }),
        paymentMode: paymentModeParam(paymentMode) // [CASH-MODE]
      };
      
      const apiUrl = `${API_BASE_URL}/msbReport/getTransactions/${userId}`;
      
      const response = await axios.post(
        apiUrl,
        requestData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      if (response.status === 200 && response.data && response.data.status === 'SUCCESS') {
        const {
          transactionsList,
          summary: responseSummary,
          userLevel,
          bypassRestrictions,
          totalShops,
          filteredShops,
          isFiltered
        } = response.data.data;
        
        // [CASH-MODE] client-side guard in case the API ignores the filter
        setTransactions(filterRowsByMode(transactionsList || [], paymentMode));
        setTotalsByMode(response.data.data.totalsByMode || (responseSummary && responseSummary.totalsByMode) || null);
        setSummary(responseSummary || {
          totalTransactions: 0,
          totalAmount: 0,
          successfulTransactions: 0,
          failedTransactions: 0
        });
        setMsbInfo({
          userLevel,
          bypassRestrictions,
          totalShops: totalShops || 0,
          isFiltered,
          filteredShops: filteredShops || 0
        });
      } else {
        setTransactions([]);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setTransactions([]);
    }
    
    setLoading(false);
  };

  const formatDate = (dateString) => {
    try {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return format(date, 'dd-MM-yyyy HH:mm:ss');
    } catch (error) {
      return dateString || 'N/A';
    }
  };

  const handleExportCSV = () => {
    if (!transactions || transactions.length === 0) {
      setDialogMessage('No transaction data to export.');
      setOpenDialog(true);
      return;
    }

    const headers = [
      'Sl No',
      'Date & Time',
      'Customer ID',
      'Customer Name',
      'Customer VPA ID',
      'Mode', // [CASH-MODE]
      'Amount (INR)',
      'Commission %',
      'Commission Amount (INR)',
      'Merchant Amount (INR)',
      'Transaction ID',
      'Reference ID',
      'Status'
    ];

    const csvRows = [headers.join(',')];

    transactions.forEach((row, idx) => {
      const sl = row.serialNumber || idx + 1;
      const dateVal = row.dateTime || row.createdAt ? new Date(row.dateTime || row.createdAt).toLocaleString('en-IN') : 'N/A';
      const cId = row.customerId || 'N/A';
      const cName = row.customerName || 'N/A';
      const cVpa = row.customerVPA || 'N/A';
      const amt = row.amount || row.transactionAmount || 0;
      const commPct = row.commissionPercent || 1;
      const commAmt = row.commissionAmount || 0;
      const merchAmt = row.merchantAmount || 0;
      const tId = row.transactionId || 'N/A';
      const rId = row.referenceId || 'N/A';
      const status = row.status || row.settlementStatus || 'SUCCESS';

      csvRows.push([
        sl,
        `"${dateVal}"`,
        `"${cId}"`,
        `"${cName}"`,
        `"${cVpa}"`,
        paymentModeLabel(row), // [CASH-MODE]
        amt,
        commPct,
        commAmt,
        merchAmt,
        `"${tId}"`,
        `"${rId}"`,
        `"${status}"`
      ].join(','));
    });

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `MSB_Transaction_Report_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns = [
    { field: 'serialNumber', headerName: 'Sl.No', width: 70 },
    {
      field: 'dateTime',
      headerName: 'Date & Time',
      width: 170,
      renderCell: (params) => formatDate(params.value || params.row.createdAt)
    },
    { field: 'customerId', headerName: 'Customer ID', width: 130 },
    { field: 'customerName', headerName: 'Customer Name', width: 140 },
    { field: 'customerVPA', headerName: 'Customer VPA ID', width: 160 },
    // [CASH-MODE]
    { field: 'paymentMode', headerName: 'Mode', width: 90, renderCell: (params) => <PaymentModeChip row={params.row} /> },
    {
      field: 'amount',
      headerName: 'Amount (₹)',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
          ₹{(params.value || params.row.transactionAmount || 0).toFixed(2)}
        </Typography>
      )
    },
    {
      field: 'commissionPercent',
      headerName: 'Commission %',
      width: 120,
      renderCell: (params) => `${(params.value || 1)}%`
    },
    {
      field: 'commissionAmount',
      headerName: 'Commission Amount (₹)',
      width: 160,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 700, color: '#16a34a' }}>
          ₹{(params.value || 0).toFixed(2)}
        </Typography>
      )
    },
    {
      field: 'merchantAmount',
      headerName: 'Merchant Amount (₹)',
      width: 160,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0284c7' }}>
          ₹{(params.value || 0).toFixed(2)}
        </Typography>
      )
    },
    { field: 'transactionId', headerName: 'Transaction ID', width: 170 },
    { field: 'referenceId', headerName: 'Reference ID', width: 170 },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      renderCell: (params) => {
        const val = (params.value || params.row.settlementStatus || 'SUCCESS').toUpperCase();
        return (
          <Chip
            label={val}
            color={val === 'SUCCESS' ? 'success' : 'error'}
            size="small"
            sx={{ fontWeight: 700 }}
          />
        );
      }
    }
  ];

  const getSelectedShopName = () => {
    if (!selectedShop) return '';
    const shop = shops.find(s => s.shopId === selectedShop);
    return shop ? shop.businessName : '';
  };

  // [CASH-MODE] prefer API totals, fall back to loaded rows
  const modeTotals = totalsByMode || totalsByModeFromRows(transactions, (r) => r.amount || r.transactionAmount || 0);
  const modeAmountKey = modeTotals?.UPI?.totalAmount !== undefined ? 'totalAmount' : 'amount';

  return (
    <Box sx={{ position: 'relative', padding: 2 }}>
      <BackButton />
      <br /><br />

      <Paper elevation={3} sx={{ p: 3, pr: 4, mb: 4, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
            Q-PAY Centre (MSB) Detailed Transaction Report
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant="contained"
              color="success"
              startIcon={<DownloadIcon />}
              onClick={handleExportCSV}
              sx={{ fontWeight: 700, height: 40 }}
            >
              Export CSV
            </Button>
          </Box>
        </Box>

        {selectedShop && (
          <Box sx={{ mb: 2 }}>
            <Chip 
              label={`Filtered by Shop: ${getSelectedShopName()}`}
              color="primary" 
              variant="outlined"
              onDelete={handleResetFilter}
              sx={{ fontWeight: 600 }}
            />
          </Box>
        )}

        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Start Date"
              type="date"
              size="small"
              fullWidth
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="End Date"
              type="date"
              size="small"
              fullWidth
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={2}>{/* [CASH-MODE] was sm={4} to fit mode filter */}
            <FormControl size="small" fullWidth disabled={shopsLoading}>
              <InputLabel>Filter by Shop</InputLabel>
              <Select
                value={selectedShop}
                label="Filter by Shop"
                onChange={handleShopChange}
              >
                <MenuItem value="">All Shops</MenuItem>
                {shops.map((shop) => (
                  <MenuItem key={shop.shopId} value={shop.shopId}>
                    {shop.businessName} - {shop.city}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {/* [CASH-MODE] */}
          <Grid item xs={12} sm={2}>
            <PaymentModeFilter value={paymentMode} onChange={setPaymentMode} fullWidth />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={fetchTransactionData}
              disabled={loading}
              fullWidth
              sx={{ height: 40, borderRadius: 2 }}
            >
              {loading ? 'Loading...' : 'Apply Filter'}
            </Button>
          </Grid>
        </Grid>

        {/* Summary Metrics */}
        {transactions.length > 0 && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={3}>
              <Paper elevation={2} sx={{ p: 2, textAlign: 'center', borderRadius: 2.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                  TOTAL TRANSACTIONS
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, color: '#0f172a' }}>
                  {summary.totalTransactions}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Paper elevation={2} sx={{ p: 2, textAlign: 'center', borderRadius: 2.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                  TOTAL AMOUNT
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, color: '#0284c7' }}>
                  ₹{(summary.totalAmount || 0).toFixed(2)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Paper elevation={2} sx={{ p: 2, textAlign: 'center', backgroundColor: '#f0fdf4', borderRadius: 2.5 }}>
                <Typography variant="caption" color="success.main" sx={{ fontWeight: 700 }}>
                  SUCCESSFUL
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, color: '#16a34a' }}>
                  {summary.successfulTransactions}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Paper elevation={2} sx={{ p: 2, textAlign: 'center', backgroundColor: '#fef2f2', borderRadius: 2.5 }}>
                <Typography variant="caption" color="error.main" sx={{ fontWeight: 700 }}>
                  FAILED
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, color: '#dc2626' }}>
                  {summary.failedTransactions}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* [CASH-MODE] */}
        {transactions.length > 0 && (
          <PaymentModeTotals totals={modeTotals} amountKey={modeAmountKey} title="Transactions" sx={{ mb: 2 }} />
        )}

        {/* Data Table Grid */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 250 }}>
            <CircularProgress color="primary" size={50} />
            <Typography variant="body1" sx={{ ml: 2 }}>Loading MSB Transactions...</Typography>
          </Box>
        ) : (
          <Box sx={{ height: 600, width: '100%', mt: 2 }}>
            <DataGrid
              rows={transactions}
              columns={columns}
              pageSize={25}
              rowsPerPageOptions={[25, 50, 100]}
              disableSelectionOnClick
              loading={loading}
              pagination
              sx={{
                borderRadius: 2,
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#f8fafc',
                  fontWeight: 700,
                },
              }}
            />
          </Box>
        )}

        <Dialog open={openDialog} onClose={handleDialogClose}>
          <DialogTitle>Message</DialogTitle>
          <DialogContent>
            <Typography>{dialogMessage}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose} color="primary">
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Box>
  );
};

export default TotalMsbTransactionReport;